/**
 * Shared decision-cadence lookup (§B.3/§B.6: male decides every 60s, female
 * every 30s) — an exact match against the canonical, closed-set `role`
 * field (`RoleLocation.role`, e.g. "male_equity"/"female_crypto"), the same
 * field `config.ts`'s `roleLabel()` switches on for these exact two values.
 * An unrecognized role degrades to the shorter (30s) cadence rather than
 * guessing high.
 *
 * Extracted from `panels/vitals.ts`'s original `decisionCadenceMs`
 * (2026-09-28 fix): `brain/BrainPanel.ts`'s `schedulePlayback` used to look
 * up a `Record<"male" | "female", number>` by the *full* `organism_id`
 * ("male-equity-founder-0001"), which never matches a bare "male"/"female"
 * key and always fell through to the 30s default — so male playback paced
 * at 30s instead of its real 60s cadence. The first fix made this a
 * substring match so it would tolerate either shape (bare role or full
 * organism_id); this version goes further (code-review finding) and has
 * both call sites pass the canonical `role` field instead, so the match can
 * be exact against a closed, two-value set rather than a substring
 * heuristic a future role/organism-id naming choice could defeat by
 * coincidence (e.g. any id that happens to contain both "male" and
 * "female" as substrings).
 */
export function decisionCadenceMs(role: string): number {
  switch (role) {
    case "male_equity":
      return 60_000;
    case "female_crypto":
      return 30_000;
    default:
      // Never silent (code-review finding): staying quiet about an
      // unrecognized role is exactly the failure class that produced the
      // original bug this function replaced (a lookup silently falling
      // through to a wrong default with nothing to notice it). A typo'd
      // role, a future third organism, or a caller regressing to pass an
      // organism_id again should all be visible, even though the fallback
      // value itself (30s, never a guess) doesn't change.
      console.warn(`decisionCadenceMs: unrecognized role "${role}", defaulting to 30s`);
      return 30_000;
  }
}
